/**
 * Text size — a per-admin UI scale for the redesign.
 *
 * The redesign hard-codes hundreds of px font sizes inline, so there's no
 * central text token to scale. Instead we apply a CSS `zoom` to the app
 * root (#root), which scales the whole interface — text and spacing
 * together — like the browser's own zoom. Persisted in localStorage so each
 * admin keeps their preference (parallel to "tts.theme" / "tts.redesign.v2").
 *
 *   useFontScale() → [scale, setFontScale]
 *     scale        — 'sm' | 'md' | 'lg' | 'xl'   (md = 100%, the design default)
 *     setFontScale — persist + broadcast + apply
 *
 * Applied to the DOM by applyFontScale(scale); index.js calls it once at
 * boot so the preference is in place before React mounts (no flash), and
 * the hook keeps it in lockstep afterwards.
 *
 * Storage key namespaced as "tts.font-scale" (parallel to "tts.theme").
 */
import { useEffect, useState } from 'react';

const KEY = 'tts.font-scale';
const EVENT = 'tts:font-scale-changed';

// zoom multiplier per level. `md` is the untouched design default.
export const FONT_SCALES = { sm: 0.9, md: 1.0, lg: 1.1, xl: 1.2 };
const VALID = new Set(Object.keys(FONT_SCALES));

/* ─── Persistence + same-tab event bus ─────────────────────────────── */

export function getFontScale() {
  try {
    const v = localStorage.getItem(KEY);
    return VALID.has(v) ? v : 'md';
  } catch (_e) {
    return 'md';
  }
}

export function setFontScale(scale) {
  const next = VALID.has(scale) ? scale : 'md';
  try {
    localStorage.setItem(KEY, next);
    window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
  } catch (_e) { /* private browsing, etc. */ }
}

/* ─── DOM application — CSS zoom on #root ──────────────────────────── */

/**
 * Apply the scale to the app root via CSS `zoom`. Idempotent — safe to
 * call repeatedly. Targets #root (the React mount + Tailwind `important`
 * scope) so the whole redesign — including the dropdown that hosts this
 * selector — scales together. `md` clears the override rather than pinning
 * zoom at 1.
 */
export function applyFontScale(scale) {
  if (typeof document === 'undefined') return;
  const root = document.getElementById('root');
  if (!root) return;
  const z = FONT_SCALES[VALID.has(scale) ? scale : 'md'];
  root.style.zoom = z === 1 ? '' : String(z);
}

/* ─── React hook ──────────────────────────────────────────────────── */

export function useFontScale() {
  const [scale, setScale] = useState(getFontScale);

  // Re-read on storage events (cross-tab) and our custom event (same-tab).
  useEffect(() => {
    const sync = () => setScale(getFontScale());
    window.addEventListener('storage', sync);
    window.addEventListener(EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(EVENT, sync);
    };
  }, []);

  // Keep the DOM in lockstep with the stored scale.
  useEffect(() => {
    applyFontScale(scale);
  }, [scale]);

  return [scale, setFontScale];
}
